import { type FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIsDocumentTransferPending } from "web_myplann/Api/Document/useIsDocumentTransferPending";
import { useOpenDocumentMutation } from "web_myplann/Api/Document/useOpenDocumentMutation";
import { usePinnableDocumentsQuery } from "web_myplann/Api/Document/usePinnableDocumentsQuery";
import type {
	ClientPortfolioDetailRelatedResponseDTO,
	PortfolioDocumentListItemDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { DetailRemindersSection } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRemindersSection";
import { DocumentPinDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/DocumentPinDrawer";
import { PortfolioRelatedPinDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PortfolioRelatedPinDrawer";
import { PropertyRelatedPinDrawer } from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/PropertyRelatedPinDrawer";
import type {
	PinKind,
	RelatedPinKind,
} from "web_myplann/Portfolio/Components/Redesign/Detail/Pin/pinKind";
import { RelatedCard } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedCard";
import { getReminderEntity } from "web_myplann/Portfolio/Helpers/getReminderEntity";
import { mapDocumentLinkToIds } from "web_myplann/Portfolio/Helpers/mapDocumentLinkToIds";
import { pinnedDocumentSubtitle } from "web_myplann/Portfolio/Helpers/pinnedDocumentSubtitle";
import {
	relatedByPinKind,
	relatedTitle,
} from "web_myplann/Portfolio/Helpers/relatedByPinKind";
import type { DocumentLink } from "web_myplann/Portfolio/Types/documentLink";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";

type Props = {
	related: ClientPortfolioDetailRelatedResponseDTO[];
	documents: PortfolioDocumentListItemDTO[] | undefined;
} & (
	| { itemKind: PortfolioItemKind.Portfolio; portfolioId: number }
	| { itemKind: PortfolioItemKind.Property; propertyId: number }
);

export const RelatedSectionRedesign: FC<Props> = (props) => {
	const { related, documents = [] } = props;
	const navigate = useNavigate();
	const { t } = useResource();
	const { mutate: openDocument } = useOpenDocumentMutation();
	const isProcessing = useIsDocumentTransferPending();
	const [pinKind, setPinKind] = useState<RelatedPinKind>("contract");
	const [isPinOpen, setIsPinOpen] = useState(false);
	const [isDocumentPinOpen, setIsDocumentPinOpen] = useState(false);
	const [isPinnableRequested, setIsPinnableRequested] = useState(false);

	const {
		data: documentCandidates = [],
		isLoading: isCandidatesLoading,
		isSuccess: isCandidatesLoaded,
	} = usePinnableDocumentsQuery(isPinnableRequested);

	const documentLink: DocumentLink =
		props.itemKind === PortfolioItemKind.Property
			? { kind: PortfolioItemKind.Property, id: props.propertyId }
			: { kind: PortfolioItemKind.Portfolio, id: props.portfolioId };

	const openPin = (kind: PinKind) => {
		if (kind === "document") {
			setIsPinnableRequested(true);
			const showUploadDirectly =
				isCandidatesLoaded &&
				documents.length === 0 &&
				documentCandidates.length === 0;
			if (showUploadDirectly) {
				navigate(
					Routing.Portfolio.DocumentUpload.path(
						mapDocumentLinkToIds(documentLink),
					),
				);
			} else {
				setIsDocumentPinOpen(true);
			}
			return;
		}

		setPinKind(kind);
		setIsPinOpen(true);
	};

	const handleOpenDocument = (document: PortfolioDocumentListItemDTO) => {
		if (isProcessing) {
			return;
		}
		openDocument({
			id: document.id,
			fallbackTitle: document.title ?? undefined,
		});
	};

	const relatedContracts = relatedByPinKind(related, "contract");
	const relatedProperties = relatedByPinKind(related, "property");
	const relatedServices = relatedByPinKind(related, "service");

	const openRelatedPortfolio = (
		item: ClientPortfolioDetailRelatedResponseDTO,
	) =>
		navigate(
			Routing.Portfolio.PortfolioDetail.path(
				item.relatedPortfolio?.myplannPortfolioId?.toString() ?? "",
			),
		);

	return (
		<>
			<RelatedCard
				kind="document"
				items={documents}
				getLabel={(document) => document.title}
				getSubtitle={pinnedDocumentSubtitle}
				onItemClick={handleOpenDocument}
				onPin={openPin}
			/>

			<DetailRemindersSection
				entity={getReminderEntity(
					props.itemKind,
					props.itemKind === PortfolioItemKind.Property
						? props.propertyId
						: props.portfolioId,
				)}
			/>

			<RelatedCard
				kind="contract"
				items={relatedContracts}
				getLabel={(item) => relatedTitle(item, "contract", t)}
				getIconSource={(item) => ({ domain: item.relatedPortfolio?.domain })}
				onItemClick={openRelatedPortfolio}
				onPin={openPin}
			/>

			<RelatedCard
				kind="property"
				items={relatedProperties}
				getLabel={(item) => relatedTitle(item, "property", t)}
				getIconSource={(item) => ({ propertyType: item.relatedProperty?.type })}
				onItemClick={(item) =>
					navigate(
						Routing.Portfolio.PropertyDetail.path(
							item.relatedProperty?.myplannPropertyId?.toString() ?? "",
						),
					)
				}
				onPin={openPin}
			/>

			<RelatedCard
				kind="service"
				items={relatedServices}
				getLabel={(item) => relatedTitle(item, "service", t)}
				getIconSource={(item) => ({ domain: item.relatedPortfolio?.domain })}
				onItemClick={openRelatedPortfolio}
				onPin={openPin}
			/>

			<DocumentPinDrawer
				open={isDocumentPinOpen}
				onClose={() => setIsDocumentPinOpen(false)}
				link={documentLink}
				documents={documents}
				candidates={documentCandidates}
				isLoading={isCandidatesLoading}
			/>

			{props.itemKind === PortfolioItemKind.Property ? (
				<PropertyRelatedPinDrawer
					kind={pinKind}
					open={isPinOpen}
					onClose={() => setIsPinOpen(false)}
					related={related}
					propertyId={props.propertyId}
				/>
			) : (
				<PortfolioRelatedPinDrawer
					kind={pinKind}
					open={isPinOpen}
					onClose={() => setIsPinOpen(false)}
					related={related}
					portfolioId={props.portfolioId}
				/>
			)}
		</>
	);
};
